import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { ssoGetUsersOrganization, ssoUserRegister, whoami } from '@/api/auth.ts';
import { useNavigate } from 'react-router';
import MainLayout from '@/layouts/MainLayout/MainLayout.tsx';
import Container from '@/components/ui/Container/Container.tsx';
import routes from '@/constants/routes.ts';
import Spinner from '@/components/ui/Spinner/Spinner.tsx';
import classNames from 'classnames';
import styles from './SsoRegister.module.scss';
import Button from '@/components/ui/Button/Button.tsx';
import { countries } from '@/constants/countries.ts';
import { Form, Formik, FormikHelpers } from 'formik';
import handleResponseErrors from '@/utils/handleResponseErrors.ts';
import GlobalError from '@/components/FormikErrors/GlobalError.tsx';
import queryKeys from '@/constants/queryKeys.ts';
import { externalLinks } from '@/constants/externalLinks';
import { routeAfterAuth } from '@/utils/authRedirect.ts';

type SsoUserRegisterRequest = Omit<
	App.Data.Request.CreateOrganizationUserSsoRequest,
	'organizationId' | 'legalEntityId'
> & {
	organizationId: number | undefined;
	legalEntityId: number | undefined;
	provider: string;
};

type SsoUserRegisterPayload = App.Data.Request.CreateOrganizationUserSsoRequest & {
	provider: string;
};

const useQueryParams = () => {
	return new URLSearchParams(useLocation().search);
};

const SsoRegister: React.FC = () => {
	const navigate = useNavigate();
	const queryParams = useQueryParams();
	const token = queryParams.get('token');
	const provider = queryParams.get('provider');
	const [step, setStep] = useState(1);
	const [initialValues, setInitialValues] = useState<SsoUserRegisterRequest>({
		organizationId: undefined,
		legalEntityId: undefined,
		token: token || '',
		provider: provider || '',
	});

	const { data, isSuccess, isError, isLoading } = useQuery({
		queryKey: [queryKeys.usersOrganizations, provider, token],
		queryFn: () => ssoGetUsersOrganization({ provider: provider || '', token: token || '' }),
		enabled: !!token && !!provider,
	});
	const organizations = data?.organizations ?? [];

	useEffect(() => {
		const organization = organizations.length === 1 ? organizations[0] : undefined;
		const legalEntities = organization?.legalEntities ?? [];
		const legalEntity = legalEntities[0];

		if (isSuccess && organizations.length === 0) {
			navigate(routes.noOrganization);
		}

		if (isError) {
			navigate(routes.authError);
		}

		if (organization && legalEntities.length === 1 && legalEntity) {
			const updatedValues = {
				organizationId: organization.id,
				legalEntityId: legalEntity.id,
				token: token || '',
				provider: provider || '',
			};

			setInitialValues(prev => ({
				...prev,
				...updatedValues,
			}));
			handleSubmit(updatedValues, {} as FormikHelpers<SsoUserRegisterRequest>);
		}

		if (organization && legalEntities.length > 1) {
			setInitialValues(prev => ({
				...prev,
				organizationId: organization.id,
			}));
			setStep(2);
		}
	}, [organizations, isSuccess, isError, navigate, provider, token]);

	const handleNext = (values: SsoUserRegisterRequest, formikHelpers: FormikHelpers<SsoUserRegisterRequest>) => {
		const selectedOrganization = organizations.find(org => org.id === values.organizationId);
		const legalEntities = selectedOrganization?.legalEntities ?? [];

		if (!selectedOrganization) {
			return;
		}

		if (step === 1) {
			if (legalEntities.length <= 1) {
				const updatedValues = {
					...values,
					organizationId: selectedOrganization.id,
					legalEntityId: legalEntities[0]?.id,
				};

				return handleSubmit(updatedValues, formikHelpers);
			}

			setStep(2);
			return;
		}

		return handleSubmit(values, formikHelpers);
	};

	const handleSubmit = async (
		values: SsoUserRegisterRequest,
		formikHelpers: FormikHelpers<SsoUserRegisterRequest>,
	) => {
		if (values.organizationId === undefined || values.legalEntityId === undefined) {
			return;
		}

		const payload: SsoUserRegisterPayload = {
			...values,
			organizationId: values.organizationId,
			legalEntityId: values.legalEntityId,
		};

		try {
			const response = await ssoUserRegister(payload);

			if (response?.redirectUrl) {
				routeAfterAuth(response);
				return;
			}

			routeAfterAuth(await whoami(), externalLinks.spotnana.book);
		} catch (error) {
			handleResponseErrors(error, formikHelpers);
		}
	};

	if (
		isLoading ||
		(isSuccess && data?.organizations?.length === 1 && data?.organizations[0]?.legalEntities?.length === 1)
	) {
		return (
			<MainLayout>
				<Spinner />
			</MainLayout>
		);
	}

	return (
		<MainLayout>
			<Container>
				<Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize={true}>
					{form => {
						const selectedOrganization =
							organizations.find(org => org.id === form.values.organizationId) ??
							organizations.find(org => org.id === initialValues.organizationId);
						const selectedLegalEntities = selectedOrganization?.legalEntities ?? [];

						return (
							<Form className={styles.contentWrapper}>
								{step === 1 && (
									<>
										<h3>Choose your organization</h3>
										<GlobalError />
										<div>
											{organizations.map(({ name, countryCode, id }) => {
												const hasCountryCode = !!countryCode;
												return (
													<div key={id}>
														<div
															onClick={() => form.setFieldValue('organizationId', id)}
															className={classNames(
																styles.option,
																form.values.organizationId === id && styles.selected,
															)}
														>
															{name}
															{hasCountryCode &&
																` (${countries.find(country => country.countryCode === countryCode)?.name})`}
														</div>
													</div>
												);
											})}
										</div>
									</>
								)}
								{step === 2 && (
									<>
										<h3>Choose legal entity</h3>
										<GlobalError />
										<div>
											{selectedLegalEntities.map(({ name, id }) => {
												return (
													<div key={id}>
														<div
															onClick={() => form.setFieldValue('legalEntityId', id)}
															className={classNames(
																styles.option,
																form.values.legalEntityId === id && styles.selected,
															)}
														>
															{name}
														</div>
													</div>
												);
											})}
										</div>
									</>
								)}
								<div className={styles.buttonsWrapper}>
									{organizations.length > 1 && step !== 1 && (
										<Button size="large" onClick={() => setStep(1)}>
											Back
										</Button>
									)}
									<Button
										onClick={() => handleNext(form.values, form)}
										size="large"
										color="green"
										loading={form.isSubmitting}
										disabled={
											(step === 1 && !form.values.organizationId) ||
											(step === 2 && !form.values.legalEntityId) ||
											form.isSubmitting
										}
									>
										Continue
									</Button>
								</div>
							</Form>
						);
					}}
				</Formik>
			</Container>
		</MainLayout>
	);
};

export default SsoRegister;
